import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";
import { getTrustLogos } from "./aiLogos";
import FadeInView from "./FadeInView";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Display baselines kept in sync with the per-mode pages so the landing total
// equals the sum of the numbers shown on those pages:
//   Debate Mode page  (DebateMode.js):  debatesCompleted + DEBATE_DISPLAY_BASELINE
//   Research dashboard (Dashboard.js):  researchAnalyses + RESEARCH_DISPLAY_BASELINE
const DEBATE_DISPLAY_BASELINE = 800;
const RESEARCH_DISPLAY_BASELINE = 840;
const DISPLAY_BASELINE = DEBATE_DISPLAY_BASELINE + RESEARCH_DISPLAY_BASELINE;

export default function NewLandingTrustStrip() {
  const { darkMode } = useTheme();
  const logos = getTrustLogos(darkMode);
  /** null = loading, -1 = fetch failed */
  const [completedCount, setCompletedCount] = useState(null);

  useEffect(() => {
    let isActive = true;
    let statsStream = null;

    const fetchStats = () => {
      Promise.all([
        fetch(`${API_URL}/stats/debates-completed`, { credentials: "omit" }),
        fetch(`${API_URL}/stats/research-analyses-completed`, {
          credentials: "omit",
        }),
      ])
        .then(async ([debatesRes, researchRes]) => {
          if (!isActive) return;
          if (!debatesRes.ok || !researchRes.ok) {
            setCompletedCount(-1);
            return;
          }
          const [debatesData, researchData] = await Promise.all([
            debatesRes.json(),
            researchRes.json(),
          ]);
          if (!isActive) return;
          if (
            typeof debatesData?.count === "number" &&
            typeof researchData?.count === "number" &&
            debatesData.count >= 0 &&
            researchData.count >= 0
          ) {
            setCompletedCount(
              debatesData.count + researchData.count + DISPLAY_BASELINE,
            );
          } else {
            setCompletedCount(-1);
          }
        })
        .catch(() => {
          if (isActive) setCompletedCount(-1);
        });
    };

    fetchStats();

    statsStream = new EventSource(`${API_URL}/stats/live`);
    statsStream.addEventListener("stats", (event) => {
      if (!isActive) return;
      try {
        const data = JSON.parse(event.data);
        if (
          typeof data?.totalCompletedAnalyses === "number" &&
          data.totalCompletedAnalyses >= 0
        ) {
          setCompletedCount(data.totalCompletedAnalyses + DISPLAY_BASELINE);
        }
      } catch {
        // Keep last known value if stream payload is malformed.
      }
    });
    statsStream.onerror = () => {
      if (isActive) fetchStats();
    };

    return () => {
      isActive = false;
      statsStream?.close();
    };
  }, []);

  const metricDisplay =
    completedCount === null
      ? "…"
      : completedCount < 0
        ? "—"
        : completedCount.toLocaleString("en-US");

  return (
    <section className="trust-strip">
      <div className="trust-inner">
        <FadeInView className="trust-top">
          <div className="trust-text">
            Built for legal professionals — from solo practitioners to small firms.
          </div>
          <div className="trust-metric-card">
            <div className="trust-metric-number" aria-live="polite">
              {metricDisplay}
            </div>
            <div className="trust-metric-label">
              AI debates &amp; analyses completed
            </div>
          </div>
        </FadeInView>
        <div className="trust-divider" />
        <FadeInView className="trust-models" delay={0.1}>
          <div className="trust-models-label">Powered by leading AI systems</div>
          <div className="model-row">
            {logos.map((logo) => (
              <motion.div
                key={logo.name}
                className="model-item"
                whileHover={{ y: -4, scale: 1.06 }}
                transition={{ duration: 0.2 }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="model-icon"
                  onError={(e) => {
                    if (logo.fallback) e.currentTarget.src = logo.fallback;
                  }}
                />
                <span className="model-name">{logo.name}</span>
              </motion.div>
            ))}
          </div>
        </FadeInView>
      </div>
    </section>
  );
}
